import type { AlertCondition, AlertConditionGroup, Connective } from "@hydrologiq/shared/components/alerts"

/*
 * The draft row's conditions while they're edited: top-level conditions and groups, each
 * group with its own connective (one level deep). Conditions move between them by drag.
 */

/** One condition being edited: `field` is "assetId|metric", or "" until chosen. */
export interface DraftCondition {
  id: string
  field: string
  operator: string
  threshold: string
}

export interface DraftGroup {
  id: string
  connective: Connective
  conditions: DraftCondition[]
}

export interface DraftConditions {
  connective: Connective
  conditions: DraftCondition[]
  groups: DraftGroup[]
}

/** The drop container for top-level conditions; a group's container id is its own id. */
export const ROOT = "root"

export const flip = (c: Connective): Connective => (c === "AND" ? "OR" : "AND")

export function allConditions(draft: DraftConditions): DraftCondition[] {
  return [...draft.conditions, ...draft.groups.flatMap((g) => g.conditions)]
}

/** The container (ROOT or a group id) a condition is in, or the container an id names. */
export function containerOf(draft: DraftConditions, id: string): string | null {
  if (id === ROOT || draft.groups.some((g) => g.id === id)) return id
  if (draft.conditions.some((c) => c.id === id)) return ROOT
  return draft.groups.find((g) => g.conditions.some((c) => c.id === id))?.id ?? null
}

function itemsOf(draft: DraftConditions, container: string): DraftCondition[] {
  return container === ROOT ? draft.conditions : draft.groups.find((g) => g.id === container)?.conditions ?? []
}

function withItems(draft: DraftConditions, container: string, items: DraftCondition[]): DraftConditions {
  if (container === ROOT) return { ...draft, conditions: items }
  return { ...draft, groups: draft.groups.map((g) => (g.id === container ? { ...g, conditions: items } : g)) }
}

/**
 * Move condition `activeId` to where `overId` is: before that condition, or to the end of
 * the container `overId` names (ROOT or a group, e.g. an empty group's slot).
 */
export function moveCondition(draft: DraftConditions, activeId: string, overId: string): DraftConditions {
  const from = containerOf(draft, activeId)
  const to = containerOf(draft, overId)
  if (!from || !to || activeId === overId) return draft

  const moving = itemsOf(draft, from).find((c) => c.id === activeId)
  if (!moving) return draft
  const next = withItems(draft, from, itemsOf(draft, from).filter((c) => c.id !== activeId))

  const target = itemsOf(next, to)
  const overIndex = target.findIndex((c) => c.id === overId)
  // Moving down within one list lands after the condition it's dropped on, as the eye expects
  const movingDown = from === to && itemsOf(draft, from).findIndex((c) => c.id === activeId) <= overIndex
  const at = overIndex === -1 ? target.length : overIndex + (movingDown ? 1 : 0)
  return withItems(next, to, [...target.slice(0, at), moving, ...target.slice(at)])
}

/** Dissolve a group: its conditions move to the end of the top level. */
export function ungroup(draft: DraftConditions, groupId: string): DraftConditions {
  const group = draft.groups.find((g) => g.id === groupId)
  if (!group) return draft
  return {
    ...draft,
    conditions: [...draft.conditions, ...group.conditions],
    groups: draft.groups.filter((g) => g.id !== groupId),
  }
}

/** The API's condition group. Call after the draft is checked: every field chosen. */
export function toConditionGroup(draft: DraftConditions): AlertConditionGroup {
  const condition = (c: DraftCondition): AlertCondition => {
    const [targetAssetId, metric] = c.field.split("|")
    return { targetAssetId, metric, operator: c.operator, threshold: Number(c.threshold) }
  }
  return {
    connective: draft.connective,
    conditions: draft.conditions.map(condition),
    groups: draft.groups.map((g) => ({ connective: g.connective, conditions: g.conditions.map(condition) })),
  }
}

/** A saved alert's conditions, opened for editing. `newId` names each condition and group. */
export function fromConditionGroup(group: AlertConditionGroup, newId: (prefix: string) => string): DraftConditions {
  const condition = (c: AlertCondition): DraftCondition => ({
    id: newId("condition"),
    field: `${c.targetAssetId}|${c.metric}`,
    operator: c.operator,
    threshold: String(c.threshold),
  })
  const conditions = group.conditions.map(condition)
  return {
    connective: group.connective,
    conditions,
    groups: group.groups.map((g) => {
      const members = g.conditions.map(condition)
      return { id: newId("group"), connective: g.connective, conditions: members }
    }),
  }
}

// --- Contradictions ---

const isComplete = (c: DraftCondition) => c.field !== "" && c.threshold.trim() !== "" && Number.isFinite(Number(c.threshold))

/** Whether comparisons on one field can all hold for some value of it. */
function canAllHold(conditions: DraftCondition[]): boolean {
  let low = -Infinity
  let lowIncluded = false
  let high = Infinity
  let highIncluded = false
  const excluded = new Set<number>()
  const above = (v: number, included: boolean) => {
    if (v > low || (v === low && !included)) [low, lowIncluded] = [v, included]
  }
  const below = (v: number, included: boolean) => {
    if (v < high || (v === high && !included)) [high, highIncluded] = [v, included]
  }
  for (const c of conditions) {
    const v = Number(c.threshold)
    if (c.operator === "gt") above(v, false)
    else if (c.operator === "gte") above(v, true)
    else if (c.operator === "lt") below(v, false)
    else if (c.operator === "lte") below(v, true)
    else if (c.operator === "eq") {
      above(v, true)
      below(v, true)
    } else if (c.operator === "neq") excluded.add(v)
  }
  if (low < high) return true // a range of values, so excluding a few still leaves some
  return low === high && lowIncluded && highIncluded && !excluded.has(low)
}

/** Every way the conditions can be met: each is a set of conditions that must all hold. */
function waysToHold(draft: DraftConditions): DraftCondition[][] {
  const items: DraftCondition[][][] = [
    ...draft.conditions.map((c) => [[c]]),
    ...draft.groups.map((g) => (g.connective === "AND" ? [g.conditions] : g.conditions.map((c) => [c]))),
  ]
  if (draft.connective === "OR") return items.flat()
  return items.reduce<DraftCondition[][]>((ways, options) => ways.flatMap((w) => options.map((o) => [...w, ...o])), [[]])
}

/** The conditions on one field that clash: each clashing pair, or all of them if only the set does. */
function clashing(conditions: DraftCondition[]): DraftCondition[] {
  const byField = new Map<string, DraftCondition[]>()
  for (const c of conditions) byField.set(c.field, [...(byField.get(c.field) ?? []), c])
  return [...byField.values()].flatMap((same) => {
    if (canAllHold(same)) return []
    const pairs = same.flatMap((a, i) => same.slice(i + 1).filter((b) => !canAllHold([a, b])).flatMap((b) => [a, b]))
    return pairs.length ? pairs : same
  })
}

/**
 * Ids of conditions that contradict others, so they could never help the alert fire.
 * Only finished conditions count. A condition is flagged when every way of meeting the
 * alert that includes it is impossible, and it is one of the clashing conditions in one of
 * those ways: so in "SoC below 20 AND (SoC above 30 OR power above 5000)", only "SoC above
 * 30" is flagged. The same field on different Assets never clashes; OR never clashes.
 */
export function findContradictions(draft: DraftConditions): Set<string> {
  const complete: DraftConditions = {
    connective: draft.connective,
    conditions: draft.conditions.filter(isComplete),
    groups: draft.groups
      .map((g) => ({ ...g, conditions: g.conditions.filter(isComplete) }))
      .filter((g) => g.conditions.length > 0),
  }
  const ways = waysToHold(complete)
  // One level of groups keeps this small; don't stall the editor on a pathological draft
  if (ways.length > 512) return new Set()

  const useful = new Set<string>()
  const clashes = new Set<string>()
  for (const way of ways) {
    const conflicting = clashing(way)
    if (conflicting.length === 0) way.forEach((c) => useful.add(c.id))
    else conflicting.forEach((c) => clashes.add(c.id))
  }
  return new Set([...clashes].filter((id) => !useful.has(id)))
}
